import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { get as _get } from "lodash";
import { CONFIG_BASE } from "../constants/configs";
import { global } from "../services/GlobalController";
import { TokenContract, NameServiceContract } from "../helpers/contracts";
import { fetchCommonPairs } from "../helpers/pairs";

// polling pauses while the tab is hidden (helpers/queryClient.js)
const BLOCK_HEIGHT_REFRESH = 30 * 1000;
const KOINDX_BALANCE_REFRESH = 60 * 1000;
const PAIRS_REFRESH = 30 * 1000;

export const useBlockHeight = () =>
  useQuery({
    queryKey: ["blockHeight"],
    queryFn: async () => _get(await global.getStatus(), "result.headBlock", null),
    refetchInterval: BLOCK_HEIGHT_REFRESH,
  });

// "koin" and "vhp" are names, their contract address comes from the config or the name service
const resolveTokenAddress = async (address) => {
  const known = _get(CONFIG_BASE, "tokens.nameservices", {});
  if (known[address]) {
    return known[address];
  }
  if (
    _get(CONFIG_BASE, "chain.nameservice", []).includes(address) &&
    _get(CONFIG_BASE, "chain.contracts.nameservice", "") != ""
  ) {
    const { result } = await NameServiceContract().functions.get_address({ name: address });
    return _get(result, "value.address", "");
  }
  return address;
};

/**
 * Raw balance (no decimals) of a token, read through the configured RPC nodes
 */
export const fetchTokenBalance = async (tokenAddress, owner) => {
  const contract = TokenContract(await resolveTokenAddress(tokenAddress));
  const { result } = await contract.functions.balanceOf({ owner });
  return String(_get(result, "value", "0"));
};

// every balance shares the ["balance", owner, token] cache, the same token is read once for all components
export const useTokenBalance = (tokenAddress, owner) =>
  useQuery({
    queryKey: ["balance", owner, tokenAddress],
    queryFn: () => fetchTokenBalance(tokenAddress, owner),
    enabled: Boolean(tokenAddress && owner),
  });

export const KOINDX_TOKEN = _get(CONFIG_BASE, "contracts.koindx_token.address", "");

// KOINDX balance of the account drawer; it shares the balance cache with the swap and liquidity panels
export const useKoindxBalance = (owner, enabled) =>
  useQuery({
    queryKey: ["balance", owner, KOINDX_TOKEN],
    queryFn: () => fetchTokenBalance(KOINDX_TOKEN, owner),
    enabled: Boolean(owner && enabled && KOINDX_TOKEN),
    refetchInterval: KOINDX_BALANCE_REFRESH,
  });

// pairs that can route a trade between two tokens, with their reserves (KoinDX API unless disabled in settings)
export const useCommonPairs = (tokenA, tokenB) => {
  const useApi = useSelector((state) => state.settings.useapi) !== false;
  return useQuery({
    queryKey: ["commonPairs", _get(tokenA, "address", null), _get(tokenB, "address", null), useApi],
    queryFn: () => fetchCommonPairs(tokenA, tokenB, { useApi }),
    enabled: Boolean(tokenA && tokenB),
    refetchInterval: PAIRS_REFRESH,
  });
};
